import {inject, Inject, Injectable, signal} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {TuningNameModel} from "../models/Tuning-Name-model";
import {MatDialog} from "@angular/material/dialog";
import {NotificationService} from "./Notification.service";
import {DeleteDialogComponent} from "../helpers/delete-dialog/delete-dialog.component";


export interface CorrectTuningNames {
    correctTuningNames: string[];
}

export interface TuningNameValidationErrors {
    correctTuningNames?: string;
    acceptedTuningNames?: string;
    tuningOrder?: string;
    usedAt?: string;
}

@Injectable({
        providedIn: "root"
    }
)

export class TuningNamesTablesService {
    private url = `${this.API_URL}`;

    public tuningNames = signal<TuningNameModel[]>([]);
    public tuningIndex = signal<number | null>(null);
    public editInputRow = signal(false);
    public newInputRow = signal<TuningNameModel>({} as TuningNameModel);
    public isLoading = signal<boolean>(false);
    public isAddingNewTuning = signal<boolean>(false);
    public notificationService = inject(NotificationService);
    dialog = inject(MatDialog);

    public usedAtOptions = ['ECU', 'TCU'];


    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }

    getTuningNamesTable() {
        this.isLoading.set(true);
        this.httpClient.get<TuningNameModel[]>(`${this.url}/tuningNames`).subscribe(value => {
            this.tuningNames.set(value as TuningNameModel[]);
            this.isLoading.set(false);
        })
    }

    validateTuningName(row: TuningNameModel, currentId?: number): TuningNameValidationErrors {
        const errors: TuningNameValidationErrors = {};
        const correctName = row.correctTuningNames?.trim() ?? '';
        if (!correctName) {
            errors.correctTuningNames = 'Correct Tuning Name is required.';
        } else if (this.tuningNames().some(existing =>
            existing.id !== currentId &&
            existing.correctTuningNames?.trim().toLowerCase() === correctName.toLowerCase()
        )) {
            errors.correctTuningNames = 'Tuning name already exists.';
        }

        if (!row.acceptedTuningNames?.trim()) {
            errors.acceptedTuningNames = 'Accepted Tuning Names are required.';
        }

        const rawOrder = row.tuningOrder as number | string | null | undefined;
        const order = Number(rawOrder);
        if (rawOrder === null || rawOrder === undefined ||
            (typeof rawOrder === 'string' && rawOrder.trim() === '') ||
            !Number.isInteger(order) || order < 0) {
            errors.tuningOrder = 'Tuning order must be zero or greater.';
        }

        const usedAt = row.usedAt?.trim() ?? '';
        if (!usedAt) {
            errors.usedAt = 'Used At is required.';
        } else if (!this.usedAtOptions.includes(usedAt)) {
            errors.usedAt = 'Used At must be ECU or TCU.';
        }
        return errors;
    }

    isValidTuningName(row: TuningNameModel, currentId?: number): boolean {
        return Object.keys(this.validateTuningName(row, currentId)).length === 0;
    }

    private validPayload(row: TuningNameModel, currentId?: number) {
        const errors = this.validateTuningName(row, currentId);
        if (Object.keys(errors).length > 0) {
            this.notificationService.showErrorMessage(Object.values(errors)[0], 'Validation Error');
            return null;
        }
        return {
            correctTuningNames: row.correctTuningNames.trim(),
            acceptedTuningNames: row.acceptedTuningNames?.trim() ?? '',
            tuningOrder: Number(row.tuningOrder),
            usedAt: row.usedAt
        };
    }

    addTuningNames() {
        if (this.isAddingNewTuning()) return;
        const payload = this.validPayload(this.newInputRow());
        if (!payload) return;

        this.isAddingNewTuning.set(true);
        this.httpClient.post<TuningNameModel>(`${this.url}/tuningNames`, payload, {
            observe: 'response'
        }).subscribe({
            next: response => {
                this.isAddingNewTuning.set(false);
                if (response.status === 201 && response.body) {
                    this.tuningNames.update(rows => [...rows, response.body!]);
                    this.resetNewInputRow();
                    this.editInputRow.set(false);
                    this.notificationService.showSuccessMessage('Successfully added', 'Successfully added');
                }
            },
            error: (error: HttpErrorResponse) => {
                this.isAddingNewTuning.set(false);
                this.showSaveError(error);
            }
        });
    }

    resetNewInputRow() {
        this.newInputRow.set({} as TuningNameModel);
    }

    openNewRow() {
        this.editInputRow.set(!this.editInputRow());
        this.resetNewInputRow();
    }

    resetComponentState() {
        this.editInputRow.set(false);
        this.tuningIndex.set(null);
        this.resetNewInputRow();
        this.isAddingNewTuning.set(false);
    }

    updateTuningName(row: TuningNameModel) {
        if (this.isAddingNewTuning()) return;
        const payload = this.validPayload(row, row.id);
        if (!payload) return;

        this.isAddingNewTuning.set(true);
        this.httpClient.put<TuningNameModel>(`${this.url}/tuningNames/${row.id}`, payload, {
            observe: 'response'
        }).subscribe({
            next: response => {
                this.isAddingNewTuning.set(false);
                if (response.status === 200 && response.body) {
                    this.tuningNames.update(rows =>
                        rows.map(existing => existing.id === row.id ? response.body! : existing)
                    );
                    this.tuningIndex.set(null);
                    this.notificationService.showSuccessMessage('Successfully updated', 'Successfully updated');
                }
            },
            error: (error: HttpErrorResponse) => {
                this.isAddingNewTuning.set(false);
                this.showSaveError(error);
            }
        });
    }

    private showSaveError(error: HttpErrorResponse): void {
        const body = error.error;
        if (body?.errors && typeof body.errors === 'object') {
            const messages = Object.values(body.errors).map(String).filter(Boolean);
            if (messages.length) {
                messages.forEach(fieldMessage => this.notificationService.showErrorMessage(fieldMessage, 'Error'));
                return;
            }
        }
        const message = typeof body?.message === 'string' && body.message.trim()
            ? body.message.trim()
            : typeof body?.detail === 'string' && body.detail.trim()
                ? body.detail.trim()
                : typeof body === 'string' && body.trim()
                    ? body.trim()
                    : null;
        const fallback = error.status === 409
            ? 'Tuning name or order already exists.'
            : error.status === 400 ? 'Invalid tuning name data.' : 'Could not save tuning name.';
        this.notificationService.showErrorMessage(message ?? fallback, 'Error');
    }


    deleteTuningNames(tuningName: TuningNameModel) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete Ecu?"
        })
        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteTuningNamesRequest(tuningName.id);
            }
        })
    }

    deleteTuningNamesRequest(id: number) {
        this.httpClient.delete(`${this.url}/tuningNames/${id}`, {observe: "response"}).subscribe(response => {
            if (response.status === 200) {
                this.tuningNames.update((tuningNames) => tuningNames.filter((tuningName) => tuningName.id != id));
                this.notificationService.showSuccessMessage("Successfully deleted", `Successfully deleted tuning ${id}`)
            }
        })
    }

    getListOfCorrectTuningNames() {
        return this.httpClient.get<CorrectTuningNames>(`${this.url}/tuningNames/listCorrectTuningNames`)
    }

}
